import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastPosition } from "react-toastify";
import { emailValidationPattern } from "src/schema/validations";

//import { useDebounceCallback } from "usehooks-ts";
import { USER_TYPE } from "@constants/Bookings";
import { CONSTANTS } from "@constants/index";
import { CONTACT_TYPE } from "@constants/register";
import { useLocationContext } from "@contexts/location/LocationContext";
import { useBooking } from "@hooks/useBooking/useBooking";
import { AddGuestsProps } from "@models/components/booking.model";
import { AddGuestSubmitType } from "@models/services/booking";
import {
  AddGuestType,
  CheckParticipantErrors,
  GuestFormData,
  GuestFormField,
  SelectGuestType,
} from "@models/shared/shared.model";
import { LocalizationObjectType } from "@models/utils/localisation.models";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";
import commonUtils from "@utils/common.utils";
export const useAddGuests = ({ hideLiteProfile, slotInfo }: AddGuestsProps) => {
  const { locationDetails } = useLocationContext();
  const { t } = useTranslation();
  const ADDGUESTMODALTABS = useMemo(
    () => [
      { disabled: false, id: USER_TYPE.MEMBER, label: t("addGuest.members") },
      { disabled: false, id: USER_TYPE.GUEST, label: t("addGuest.guests") },
    ],
    [t]
  );

  const ADDGUESTMODALFIELDS: GuestFormField[] = [
    {
      labelName: t("firstName"),
      maxLength: 50,
      name: "firstName",
      type: "text",
    },
    { labelName: t("lastName"), maxLength: 50, name: "lastName", type: "text" },
    { labelName: t("mainEmail"), maxLength: 50, name: "email", type: "text" },
  ];

  const clubFilterList = useMemo(
    () => [
      {
        id: locationDetails?.clubId || "",
        label: `${applyLocalization(locationDetails?.title as LocalizationObjectType)} ${t("members")}`,
      },
      {
        id: USER_TYPE.otherContacts,
        label: t(USER_TYPE.otherContacts),
      },
    ],
    [locationDetails, t]
  );

  const [filteredClub, setFilteredClub] = useState<string>(
    locationDetails?.clubId || ""
  );

  const dispatch = useDispatch();
  const { getSelectedResource } = useBooking();

  const {
    bookingEditData,
    checkparticipantErrors,
    guestList,
    isEditBooking,
    selectedDate,
    selectedSlot,
  } = useSelector((state: RootState) => state.booking);

  const [showAddGuestModal, setShowAddGuestModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(USER_TYPE.MEMBER);
  const [showMaxExceededMessage, setShowMaxExceededMessage] =
    useState<boolean>(false);
  const [selectedGuest, setSelectedGuest] = useState<SelectGuestType | null>(
    null
  );
  const [userFavouriteList, setUserFavouriteList] = useState<AddGuestType[]>(
    []
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addGuestError, setAddGuestError] = useState<string>("");
  const [guestMessage, setGuestMessage] = useState<string>("");
  const [guestFormData, setGuestFormData] = useState<GuestFormData>({
    email: "",
    firstName: "",
    lastName: "",
  });
  const [emailError, setEmailError] = useState<string>("");

  const maxParticipants = selectedSlot?.slot?.max ?? null;
  const maxExceeded = useMemo(
    () => !!(maxParticipants && guestList.length >= maxParticipants),
    [maxParticipants, guestList.length]
  );

  const selectedSlotInfo = useMemo(
    () => getSelectedResource(slotInfo),
    [slotInfo, getSelectedResource]
  );

  useEffect(() => {
    if (locationDetails?.clubId) {
      setFilteredClub(locationDetails.clubId);
    }
  }, [locationDetails?.clubId]);

  const allowBringGuest = useMemo(
    () =>
      (selectedSlotInfo?.policyInfo?.allowBringGuest &&
        selectedSlotInfo?.slotInfo?.slots?.allowedGuestTimings.length > 0) ??
      false,
    [selectedSlotInfo]
  );

  const isGuestTimingAllowed = useMemo(
    () =>
      selectedSlotInfo?.slotInfo?.slots?.allowedGuestTimings?.includes(
        selectedSlot?.slot?.starts ?? ""
      ) ?? false,
    [selectedSlotInfo, selectedSlot]
  );

  const quotaExceeded = useMemo(() => {
    const quota = selectedSlotInfo?.quotaInfo?.MAX_GUEST_TIMES;
    return !!(quota && !quota.ignoreUG && quota.quotaTotal <= quota.quotaUsed);
  }, [selectedSlotInfo]);

  const isGuestAdditionDisabled = !(allowBringGuest && isGuestTimingAllowed);

  const resetGuestForm = useCallback(() => {
    setGuestFormData({ email: "", firstName: "", lastName: "" });
    setSelectedGuest(null);
    setAddGuestError("");
    setEmailError("");
    setShowMaxExceededMessage(false);
  }, []);

  const openAddGuestModal = useCallback(() => {
    setShowAddGuestModal(true);
    resetGuestForm();
  }, [resetGuestForm]);

  const closeAddGuestModal = useCallback(() => {
    setShowAddGuestModal(false);
    resetGuestForm();
  }, [resetGuestForm]);

  const loadOptions = useCallback(
    async (inputValue: string) => {
      if (!inputValue.trim()) return [];

      try {
        const response = await API_SERVICES.BOOKING.GET_GUEST_LIST(
          inputValue,
          filteredClub,
          hideLiteProfile
        );

        const guests: AddGuestType[] = response.data
          .map(
            (guest: SelectGuestType): AddGuestType => ({
              ...guest,
              name: commonUtils.getCardNameByType(guest),
            })
          )
          .filter(
            (guest: AddGuestType) =>
              !guestList.some(p => p.profileUuid === guest.profileUuid)
          );

        const nameCount: Record<string, number> = {};
        const nameYearCount: Record<string, number> = {};

        guests.forEach(g => {
          const normalizedName = (g.name ?? "").trim().toLowerCase();

          nameCount[normalizedName] = (nameCount[normalizedName] || 0) + 1;

          const yearKey = `${normalizedName}-${g.yearOfBirth ?? ""}`;
          nameYearCount[yearKey] = (nameYearCount[yearKey] || 0) + 1;
        });

        return guests.map(g => {
          const baseName = (g.name ?? "").trim();
          const normalizedName = baseName.toLowerCase();

          const isCompanyWithoutContact =
            g.contactType === CONTACT_TYPE.COMPANY_WITHOUT_CONTACT;

          let label = baseName;

          if (!isCompanyWithoutContact && nameCount[normalizedName] > 1) {
            const parts: string[] = [];

            if (g.yearOfBirth) {
              parts.push(g.yearOfBirth);
            }

            const key = `${normalizedName}-${g.yearOfBirth ?? ""}`;

            if (g.city && nameYearCount[key] > 1) {
              parts.push(g.city);
            }

            if (parts.length > 0) {
              label = `${baseName} (${parts.join(", ")})`;
            }
          }

          return {
            label,
            value: g.profileUuid,
            ...g,
          };
        });
      } catch {
        return [];
      }
    },
    [guestList, filteredClub, hideLiteProfile]
  );

  // Debounced version of fetchGuestList using usehooks-ts
  // const debouncedFetchGuestList = useDebounceCallback(fetchGuestList, 500);
  // // loadOptions function for AsyncSelect
  // const loadOptions = useCallback(
  //   async (inputValue: string) => {
  //     return debouncedFetchGuestList(inputValue);
  //   },
  //   [debouncedFetchGuestList]
  // );

  const buildGuestPayload = useCallback(
    (participantUuid: string | null): AddGuestSubmitType => ({
      bookingStartTime: selectedSlot?.slot?.starts ?? "",
      currentParticipantArray: guestList.map(p => p.profileUuid),
      participantUuid: participantUuid ?? "",
      resourceUUID: selectedSlot?.resourceId ?? "",
      ...(isEditBooking && bookingEditData?.bookingId
        ? { excludedBookingUUID: bookingEditData?.bookingId }
        : {}),
      bookingDate: selectedDate,
      bookingEndTime: selectedSlot?.slot?.ends ?? "",
    }),
    [selectedDate, selectedSlot, guestList]
  );

  const isGuestFormValid = useMemo(() => {
    const { email, firstName, lastName } = guestFormData;
    return (
      firstName.trim() !== "" && lastName.trim() !== "" && email.trim() !== ""
    );
  }, [guestFormData]);

  const mapCheckParticipantErrors = useCallback(
    (errors: CheckParticipantErrors[]) => {
      const result: Record<string, string> = {};
      for (const item of errors) {
        result[item?.participantUuid] = item?.error?.responseData?.quotaInfo
          ? item?.error?.responseData?.quotaInfo?.quotaMessage
          : item?.error?.message;
      }
      return result;
    },
    []
  );

  const checkParticipants = useCallback(
    async (participantUuid: string | null) => {
      setIsLoading(true);
      const postArray = buildGuestPayload(participantUuid);
      await API_SERVICES.BOOKING.CHECK_GUEST_LIST(postArray).then(response => {
        if (!response.data.status) {
          dispatch(
            bookingSlice.actions.setCheckParticipantErrors(
              mapCheckParticipantErrors(response?.data?.errors)
            )
          );
        } else {
          dispatch(bookingSlice.actions.setCheckParticipantErrors({}));
        }
        setIsLoading(false);
      });
    },
    [buildGuestPayload, dispatch]
  );

  const handleRemoveGuest = useCallback(
    (uuid: string) => {
      dispatch(bookingSlice.actions.removeGuest(uuid));
      dispatch(bookingSlice.actions.setBookingDataChanged(true));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!selectedSlot?.slot) {
      setGuestMessage("");
      return;
    }

    if (!selectedSlotInfo) {
      setGuestMessage("");
      return;
    }

    if (!allowBringGuest) {
      setGuestMessage(t("addGuest.guestRestrictionOnPolicy"));
      return;
    }

    if (!isGuestTimingAllowed) {
      const timings =
        selectedSlotInfo?.slotInfo?.slots?.allowedGuestTimings ?? [];

      setGuestMessage(
        t("addGuest.guestRestrictionOnTiming", {
          endTime: timings[timings.length - 1],
          startTime: timings[0],
        })
      );
      return;
    }

    if (quotaExceeded) {
      setGuestMessage(
        selectedSlotInfo?.quotaInfo?.MAX_GUEST_TIMES?.quotaMessage || ""
      );
      return;
    }
    setGuestMessage("");

    checkParticipants(null);
  }, [
    selectedSlot,
    selectedSlotInfo,
    allowBringGuest,
    isGuestTimingAllowed,
    quotaExceeded,
    checkParticipants,
  ]);

  const validateGuestForm = () => {
    const { email, firstName, lastName } = guestFormData;

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setAddGuestError(t("addGuest.enterAllRequiredFields"));
      return false;
    }

    if (!emailValidationPattern.test(email)) {
      setEmailError(t("addGuest.invalidEmail"));
      return false;
    }

    const emailExists = guestList.some(
      g => g.email?.trim().toLowerCase() === email.trim().toLowerCase()
    );

    if (emailExists) {
      setEmailError(t("addGuest.emailAlreadyExists"));
      return false;
    }

    return true;
  };

  const createExternalGuest = async () => {
    const response =
      await API_SERVICES.BOOKING.ADD_EXTERNAL_GUEST(guestFormData);

    if (!response.data.status) {
      setEmailError(response.data.message);
      return null;
    }

    return response.data.data; // new UUID
  };

  const addGuestToBooking = async (
    participantUuid: string,
    guestData: AddGuestType
  ) => {
    const payload = buildGuestPayload(participantUuid);

    try {
      const response = await API_SERVICES.BOOKING.ADD_GUEST(payload);

      if (response.data.status) {
        commonUtils.toastService(
          response.data.message,
          CONSTANTS.TOAST_TYPE.SUCCESS,
          CONSTANTS.TOAST_AUTO_CLOSE.THOUSAND_FIVE_HUNDRED,
          CONSTANTS.TOAST_POSITION.BOTTOM_CENTER as ToastPosition
        );

        dispatch(bookingSlice.actions.addGuest(guestData));
        dispatch(bookingSlice.actions.setBookingDataChanged(true));
        setSelectedGuest(null);
        return true;
      }

      setAddGuestError(response.data.message);
      return false;
    } catch {
      return false;
    }
  };

  const buildExternalGuestData = (uuid: string): AddGuestType => {
    const { email, firstName, lastName } = guestFormData;

    return {
      companyName: "",
      contactType: USER_TYPE.GUEST,
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      profileUuid: uuid,
    };
  };

  const isAddGuestButtonDisabled = useMemo(() => {
    if (isLoading) return true;

    if (
      (activeTab === USER_TYPE.MEMBER && !selectedGuest) ||
      (activeTab === USER_TYPE.GUEST && !isGuestFormValid)
    )
      return true;

    return false;
  }, [isLoading, activeTab, selectedGuest, isGuestFormValid]);
  const handleAddGuest = useCallback(
    async (directGuest?: SelectGuestType) => {
      setAddGuestError("");
      setEmailError("");

      if (maxExceeded) {
        setShowMaxExceededMessage(true);
        return;
      }

      setIsLoading(true);

      try {
        // MEMBER TAB
        if (activeTab === USER_TYPE.MEMBER) {
          const guestToAdd = directGuest || selectedGuest;
          if (!guestToAdd) return;

          const memberData = {
            companyName: guestToAdd.companyName || "",
            contactType: guestToAdd.contactType || USER_TYPE.MEMBER,
            email: guestToAdd.email || "",
            firstName: guestToAdd.firstName || "",
            lastName: guestToAdd.lastName || "",
            profileUuid: guestToAdd.value || guestToAdd.profileUuid || "",
          };
          await addGuestToBooking(memberData.profileUuid, memberData);
          return;
        }

        // GUEST TAB
        if (!validateGuestForm()) return;

        const newUuid = await createExternalGuest();
        if (!newUuid) return;

        const externalData = buildExternalGuestData(newUuid);
        const success = await addGuestToBooking(newUuid, externalData);

        if (success) {
          setGuestFormData({ email: "", firstName: "", lastName: "" });
        }
      } catch (err) {
        console.error("Unexpected error in handleAddGuest:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeTab,
      selectedGuest,
      maxExceeded,
      validateGuestForm,
      createExternalGuest,
      buildExternalGuestData,
      addGuestToBooking,
    ]
  );
  const getUserFavourites = useCallback(async () => {
    try {
      const response = await API_SERVICES.BOOKING.GET_USER_FAVOURITES();
      if (response?.ok) {
        setUserFavouriteList(response.data);
      } else {
        commonUtils.toastService(
          t(response?.data?.message || "common.somethingWentWrong"),
          CONSTANTS.TOAST_TYPE.ERROR,
          true,
          CONSTANTS.TOAST_POSITION.BOTTOM_CENTER as ToastPosition
        );
      }
    } catch (error) {
      console.error("Unexpected error in getUserFavourites:", error);
    }
  }, []);
  const removeUserFavourites = useCallback(async (profileUuid: string) => {
    setIsLoading(true);
    try {
      const response = await API_SERVICES.BOOKING.REMOVE_USER_FAVOURITES([
        profileUuid,
      ]);
      if (response?.ok) {
        getUserFavourites();
        commonUtils.toastService(
          t(response.data.message),
          CONSTANTS.TOAST_TYPE.SUCCESS,
          true,
          CONSTANTS.TOAST_POSITION.BOTTOM_CENTER as ToastPosition
        );
      } else {
        commonUtils.toastService(
          t(response?.data?.message),
          CONSTANTS.TOAST_TYPE.ERROR,
          true,
          CONSTANTS.TOAST_POSITION.BOTTOM_CENTER as ToastPosition
        );
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Unexpected error in remove favourite:", error);
    }
  }, []);
  const actions = useMemo(
    () => ({
      closeAddGuestModal,
      getUserFavourites,
      handleAddGuest,
      handleRemoveGuest,
      loadOptions,
      openAddGuestModal,
      removeUserFavourites,
    }),
    [
      closeAddGuestModal,
      handleAddGuest,
      handleRemoveGuest,
      loadOptions,
      openAddGuestModal,
      removeUserFavourites,
      getUserFavourites,
    ]
  );

  const config = useMemo(
    () => ({
      ADDGUESTMODALFIELDS,
      ADDGUESTMODALTABS,
    }),
    [ADDGUESTMODALFIELDS, ADDGUESTMODALTABS]
  );

  const data = useMemo(
    () => ({
      allowBringGuest,
      checkparticipantErrors,
      clubFilterList,
      filteredClub,
      guestFormData,
      guestList,
      isGuestAdditionDisabled,
      maxExceeded,
      maxParticipants,
      quotaExceeded,
      selectedGuest,
      selectedSlot,
      userFavouriteList,
    }),
    [
      allowBringGuest,
      checkparticipantErrors,
      guestFormData,
      guestList,
      isGuestAdditionDisabled,
      maxExceeded,
      maxParticipants,
      quotaExceeded,
      selectedGuest,
      selectedSlot,
      userFavouriteList,
      filteredClub,
      clubFilterList,
    ]
  );

  const setters = useMemo(
    () => ({
      resetGuestForm,
      setActiveTab,
      setAddGuestError,
      setFilteredClub,
      setGuestFormData,
      setSelectedGuest,
    }),
    []
  );

  const ui = useMemo(
    () => ({
      activeTab,
      addGuestError,
      emailError,
      guestMessage,
      isAddGuestButtonDisabled,
      isLoading,
      showAddGuestModal,
      showMaxExceededMessage,
    }),
    [
      activeTab,
      addGuestError,
      emailError,
      guestMessage,
      isAddGuestButtonDisabled,
      isLoading,
      showAddGuestModal,
      showMaxExceededMessage,
    ]
  );
  return { actions, config, data, setters, ui };
};
